import { FC, ReactElement, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PrimaryButton, Text } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { OutlookImportModal } from "../../components/modals/OutlookImportModal";
import { colorPallete } from "../../assets/color";

const styles = {
  invalidLinkContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    padding: "80px 24px",
    textAlign: "center" as const,
  },
  invalidLinkText: {
    fontFamily: "Poppins",
    fontSize: "16px",
    color: "#323232",
    maxWidth: "480px",
  },
};

/**
 * Deep-link target for the Outlook add-in (DEX-476). The add-in opens
 * /import?source=outlook&messageId={restId}&conversationId={restConvId}
 * (optionally &subject=...&from=...&mailbox=...) and this page hosts the import
 * modal.
 */
const OutlookImport: FC = (): ReactElement | null => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const source = searchParams.get("source");
  const messageId = searchParams.get("messageId");
  const conversationId = searchParams.get("conversationId");
  const subject = searchParams.get("subject") ?? undefined;
  const fromEmail = searchParams.get("from") ?? undefined;
  const mailboxEmail = searchParams.get("mailbox") ?? undefined;

  const isValidLink =
    source === "outlook" && !!messageId && !!conversationId;

  useEffect(() => {
    // The stored deep link (set while the user was signed out) has served
    // its purpose once this page mounts; clear it so a later login doesn't
    // bounce the user back here with a stale message id.
    localStorage.removeItem("LINK");
  }, []);

  if (!isValidLink) {
    return (
      <div style={styles.invalidLinkContainer}>
        <Text style={styles.invalidLinkText}>{t("outlook_invalid_link")}</Text>
        <PrimaryButton
          styles={{
            root: {
              backgroundColor: colorPallete.btBlue,
              borderColor: colorPallete.btBlue,
              borderRadius: "4px",
              fontFamily: "Poppins",
            },
          }}
          onClick={() => navigate("/home")}
        >
          {t("outlook_go_home")}
        </PrimaryButton>
      </div>
    );
  }

  return (
    <OutlookImportModal
      isModalOpen={true}
      hideModal={() => navigate("/home")}
      messageId={messageId!}
      conversationId={conversationId!}
      subject={subject}
      fromEmail={fromEmail}
      mailboxEmail={mailboxEmail}
    />
  );
};

export default OutlookImport;
